import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';

// Components
import CancelInsurance from './CancelInsurance';
import CancelInsuranceForm from './CancelInsuranceForm';
import OfferConfirmation from './OfferConfirmation';
import BasicModal from '../../../../common/src/components/BasicModal';
import { CancelledInsuranceValues } from '../../../../../types/Quotes';
import { useDispatch } from 'react-redux';
import { setTerminationInfo } from '../../store/redux/slices/offersSlice';
import { useQuote } from '../../hooks/useQuoteQueries';
import { useLocation } from 'react-router';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';

interface Props {
  riskObject: string;
  quoteId?: string;
  showModal: boolean;
  onClose: () => void;
  addToSelectedOffers: (arg0: boolean) => void;
}

enum Step {
  CONFIRM,
  CANCEL,
  INFO,
}

const AddToOffersModal: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  quoteId,
  showModal,
  onClose,
  addToSelectedOffers,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();

  const getInitialStep = () => {
    if (location.pathname.includes('selectedOffers')) return Step.INFO;
    else if (['home', 'family', 'legal'].includes(riskObject.toLowerCase()))
      return Step.CONFIRM;

    return Step.CANCEL;
  };

  const [step, setStep] = useState<Step>(getInitialStep());

  const { data: quote } = useQuote(riskObject, quoteId);

  useEffect(() => {
    setStep(getInitialStep());
  }, [riskObject]);

  const handleOnClose = () => {
    setStep(getInitialStep());
    onClose();
  };

  const handleOnFinishModal = () => {
    if (!location.pathname.includes('selectedOffers')) {
      addToSelectedOffers(false);
    }
    handleOnClose();
  };

  const handleForm = (values: CancelledInsuranceValues) => {
    trackEvent('Add notice letter in Sales Conversation');
    dispatch(setTerminationInfo({ riskObject, data: values }));
    handleOnFinishModal();
  };

  const handleOnNoTermination = () => {
    dispatch(setTerminationInfo({ riskObject, data: 'none' }));
    handleOnFinishModal();
  };

  const previousStep = () => {
    setStep((step) => step - 1);
  };

  return (
    <StyledBasicModal showModal={showModal} onClose={handleOnClose}>
      {{
        0: quote && (
          <OfferConfirmation
            quote={quote}
            onCancel={handleOnClose}
            onContinue={() => setStep(Step.CANCEL)}
          />
        ),
        1: (
          <CancelInsurance
            onCancel={handleOnClose}
            onContinue={handleOnNoTermination}
            onContinueWithForm={() => setStep(Step.INFO)}
          />
        ),
        2: (
          <CancelInsuranceForm
            withOffer={!location.pathname.includes('selectedOffers')}
            onContinue={handleForm}
            onBack={previousStep}
            onClose={onClose}
          />
        ),
      }[step] || null}
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  z-index: 12;
  > div:last-child {
    max-width: 50rem;
    padding: 2.4rem;
    margin: 0 0.8rem;
  }

  ${mq.laptop} {
    padding: 3.2rem;
  }
`;

export default AddToOffersModal;
